// web/src/types/vulndata.ts - Vulnerability data, nuclei, retirejs types

import type { ScanResult } from './finding';

export interface VulnRule {
  id: number;
  rule_id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info' | 'system';
  cve_id: string;
  cvss: number;
  cvss_vector: string;
  exploit_db: string;
  references: string;
  service_name: string;
  product_name: string;
  version_pattern: string;
  port_numbers: string;
  detection_type: 'version_match' | 'service_match' | 'banner_match' | 'version_range' | 'version_regex';
  match_condition: string;
  source: 'cve' | 'exploitdb' | 'custom';
  category: string;
  is_active: boolean;
  is_custom: boolean;
  confidence: number;
  published?: string;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface VulnDatabase {
  id: number;
  name: string;
  type: 'cve' | 'exploitdb' | 'custom';
  description: string;
  is_active: boolean;
  import_enabled: boolean;
  import_frequency: 'manual' | 'daily' | 'weekly' | 'monthly';
  import_url: string;
  total_rules: number;
  last_import: string | null;
  created_at: string;
  updated_at: string;
}

export interface VulnImport {
  id: number;
  database_id: number;
  database: {
    id: number;
    name: string;
    type: string;
  };
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  total_items: number;
  processed_items: number;
  imported_items: number;
  import_log: string;
  error_log: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface VulnConfig {
  id: number;
  default_sources: string;
  default_min_severity: string;
  default_match_mode: string;
  min_confidence: number;
  require_cvss: boolean;
  max_age: number;
  enable_version_match: boolean;
  max_concurrent_checks: number;
  check_timeout: number;
  cache_results: boolean;
  cache_ttl: number;
  updated_at: string;
}

export interface VulnDataStats {
  total_rules: number;
  cve_rules: number;
  custom_rules: number;
  exploitdb_rules: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  vuln_detections: number;
  recent_imports: VulnImport[];
  databases: VulnDatabase[];
  recently_used_rules: VulnRule[];
}

// VulnData Form types
export interface CreateVulnRuleRequest {
  rule_id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  cve_id?: string;
  cvss?: number;
  cvss_vector?: string;
  exploit_db?: string;
  references?: string;
  service_name?: string;
  product_name?: string;
  version_pattern?: string;
  port_numbers?: string;
  detection_type: 'version_match' | 'service_match' | 'banner_match';
  match_condition?: string;
  category?: string;
  confidence: number;
}

export interface UpdateVulnRuleRequest {
  name?: string;
  description?: string;
  severity?: string;
  is_active?: boolean;
  confidence?: number;
  version_pattern?: string;
  detection_type?: string;
}

export interface UpdateVulnDatabaseRequest {
  is_active?: boolean;
  import_enabled?: boolean;
  import_frequency?: string;
  import_url?: string;
}

export interface UpdateVulnConfigRequest {
  default_sources?: string;
  default_min_severity?: string;
  default_match_mode?: string;
  min_confidence?: number;
  enable_version_match?: boolean;
  max_concurrent_checks?: number;
  require_cvss?: boolean;
  max_age?: number;
  check_timeout?: number;
  cache_results?: boolean;
  cache_ttl?: number;
}

// Nuclei-specific types - NEW for Nuclei engine integration
export interface NucleiTemplate {
  id: string;
  name: string;
  author: string[];
  tags: string[];
  description: string;
  reference: string[];
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  classification: {
    'cvss-metrics'?: string;
    'cvss-score'?: number;
    'cve-id'?: string;
    'cwe-id'?: string[];
  };
  info: {
    name: string;
    author: string[];
    tags: string[];
    description: string;
    reference: string[];
    severity: string;
    classification?: any;
    metadata?: any;
  };
  requests?: any[];
  network?: any[];
  dns?: any[];
  file?: any[];
  headless?: any[];
  code?: any[];
  variables?: any;
  flow?: any[];
  javascript?: any[];
  self_contained?: boolean;
  stop_at_first_match?: boolean;
  signature?: any;
  verified?: boolean;
  max_request?: number;
  no_cluster?: boolean;
  path?: string;
  file_path?: string;
  template_id?: string;
  template_path?: string;
  template_url?: string;
}

export interface NucleiTemplateCategory {
  name: string;
  count: number;
  templates: string[];
}

export interface NucleiConfig {
  enabled: boolean;
  templates: string[];
  categories: string[];
  severities: string[];
  tags: string[];
  concurrency: number;
  rate_limit: number;
  use_detected_tech: boolean;
  custom_templates: boolean;
  template_path: string;
  exclude_templates: string[];
  include_templates: string[];
  passive_scan: boolean;
  follow_redirects: boolean;
  max_redirects: number;
  timeout: number;
  retries: number;
  bulk_size: number;
  template_threads: number;
  headless: boolean;
  system_resolvers: boolean;
  disable_clustering: boolean;
  offline_scan: boolean;
  scan_all_ips: boolean;
  ip_version: '4' | '6' | 'auto';
  interface: string;
  source_ip: string;
  config_file: string;
  report_config: string;
  severity_threshold: string;
  exclude_severity: string[];
  exclude_protocols: string[];
  exclude_tags: string[];
  include_tags: string[];
  include_ids: string[];
  exclude_ids: string[];
  author: string[];
  attack_type: string[];
  protocol: string[];
  exclude_matchers: string[];
  include_conditions: string[];
  stop_at_first_match: boolean;
  exclude_type: string[];
  include_type: string[];
  template_display: boolean;
  template_list: boolean;
  silent: boolean;
  verbose: boolean;
  very_verbose: boolean;
  debug: boolean;
  debug_requests: boolean;
  debug_response: boolean;
  proxy_url: string;
  proxy_socks_url: string;
  custom_headers: string[];
  custom_attack_type: string;
  random_agent: boolean;
  disable_redirects: boolean;
  report_format: string[];
  report_db: string;
  markdown_export: string;
  sarif_export: string;
  json_export: string;
  jsonl_export: string;
  junit_export: string;
  csv_export: string;
  disk_export: string;
  resume: string;
  stream_logs: boolean;
  no_meta: boolean;
  no_timestamp: boolean;
  env_vars: boolean;
  max_host_error: number;
  track_error: string[];
  flush_interval: number;
  stats_interval: number;
  metrics: boolean;
  metrics_port: number;
  no_color: boolean;
  no_update_templates: boolean;
  no_templates: boolean;
  validate: boolean;
  validate_export: string;
}

export interface TechnologyDetection {
  name: string;
  version?: string;
  categories: string[];
  confidence: number;
  cpe?: string;
  website?: string;
  icon?: string;
  implies?: string[];
  requires?: string[];
  excludes?: string[];
  meta?: any;
}

export interface NucleiProgress {
  scan_id: number;
  templates_total: number;
  templates_executed: number;
  templates_matched: number;
  templates_failed: number;
  current_template: string;
  current_target: string;
  templates_per_second: number;
  estimated_time_remaining: number;
  technology_detection: TechnologyDetection[];
  template_stats: {
    [category: string]: {
      total: number;
      executed: number;
      matched: number;
      failed: number;
    };
  };
}

export interface NucleiResult extends ScanResult {
  template_id: string;
  template_name: string;
  template_author: string[];
  template_tags: string[];
  template_reference: string[];
  matcher_name?: string;
  matcher_status?: boolean;
  extracted_results?: string[];
  curl_command?: string;
  interaction?: any;
  template_path?: string;
  template_url?: string;
  matched_at?: string;
  timestamp?: string;
  request?: string;
  response?: string;
  type?: string;
  matched_line?: string;
  extracted?: string[];
  meta?: any;
  classification?: {
    'cvss-metrics'?: string;
    'cvss-score'?: number;
    'cve-id'?: string;
    'cwe-id'?: string[];
  };
}

export interface TemplateStats {
  total_templates: number;
  enabled_templates: number;
  categories: NucleiTemplateCategory[];
  severities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  authors: string[];
  tags: string[];
  protocols: string[];
  last_updated: string;
}

// VulnData Constants
export const VULN_SOURCES = ['cve', 'exploitdb', 'custom'] as const;
export const DETECTION_TYPES = ['version_match', 'service_match', 'banner_match', 'version_range', 'version_regex'] as const;
export const IMPORT_FREQUENCIES = ['manual', 'daily', 'weekly', 'monthly'] as const;
export const IMPORT_STATUSES = ['pending', 'running', 'completed', 'failed', 'cancelled'] as const;

// Nuclei constants
export const NUCLEI_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export const NUCLEI_CATEGORIES = ['http', 'network', 'dns', 'file', 'headless', 'ssl', 'websocket', 'whois', 'code'] as const;
export const NUCLEI_PROTOCOLS = ['http', 'https', 'tcp', 'udp', 'dns', 'icmp'] as const;
export const NUCLEI_ATTACK_TYPES = ['http', 'network', 'dns', 'file', 'headless'] as const;

// VulnData Type guards
export const isVulnSource = (source: string): source is VulnRule['source'] => {
  return VULN_SOURCES.includes(source as any);
};

export const isDetectionType = (detectionType: string): detectionType is VulnRule['detection_type'] => {
  return DETECTION_TYPES.includes(detectionType as any);
};

export const isImportStatus = (status: string): status is VulnImport['status'] => {
  return IMPORT_STATUSES.includes(status as any);
};

// Type guards for Nuclei
export const isNucleiSeverity = (severity: string): severity is typeof NUCLEI_SEVERITIES[number] => {
  return NUCLEI_SEVERITIES.includes(severity as any);
};

export const isNucleiCategory = (category: string): category is typeof NUCLEI_CATEGORIES[number] => {
  return NUCLEI_CATEGORIES.includes(category as any);
};

// Default Nuclei configuration
export const DEFAULT_NUCLEI_CONFIG: NucleiConfig = {
  enabled: true,
  templates: [],
  categories: ['http', 'network', 'dns'],
  severities: ['medium', 'high', 'critical'],
  tags: [],
  concurrency: 25,
  rate_limit: 150,
  use_detected_tech: true,
  custom_templates: false,
  template_path: '',
  exclude_templates: [],
  include_templates: [],
  passive_scan: false,
  follow_redirects: true,
  max_redirects: 10,
  timeout: 10,
  retries: 1,
  bulk_size: 25,
  template_threads: 10,
  headless: false,
  system_resolvers: false,
  disable_clustering: false,
  offline_scan: false,
  scan_all_ips: false,
  ip_version: 'auto',
  interface: '',
  source_ip: '',
  config_file: '',
  report_config: '',
  severity_threshold: 'info',
  exclude_severity: [],
  exclude_protocols: [],
  exclude_tags: [],
  include_tags: [],
  include_ids: [],
  exclude_ids: [],
  author: [],
  attack_type: [],
  protocol: [],
  exclude_matchers: [],
  include_conditions: [],
  stop_at_first_match: false,
  exclude_type: [],
  include_type: [],
  template_display: false,
  template_list: false,
  silent: false,
  verbose: false,
  very_verbose: false,
  debug: false,
  debug_requests: false,
  debug_response: false,
  proxy_url: '',
  proxy_socks_url: '',
  custom_headers: [],
  custom_attack_type: '',
  random_agent: false,
  disable_redirects: false,
  report_format: ['json'],
  report_db: '',
  markdown_export: '',
  sarif_export: '',
  json_export: '',
  jsonl_export: '',
  junit_export: '',
  csv_export: '',
  disk_export: '',
  resume: '',
  stream_logs: false,
  no_meta: false,
  no_timestamp: false,
  env_vars: false,
  max_host_error: 30,
  track_error: [],
  flush_interval: 10,
  stats_interval: 5,
  metrics: false,
  metrics_port: 9092,
  no_color: false,
  no_update_templates: false,
  no_templates: false,
  validate: false,
  validate_export: '',
};

// VulnData Default values
export const DEFAULT_VULN_CONFIG: VulnConfig = {
  id: 1,
  default_sources: 'cve,custom',
  default_min_severity: 'medium',
  default_match_mode: 'strict',
  min_confidence: 70,
  require_cvss: true,
  max_age: 365,
  enable_version_match: true,
  max_concurrent_checks: 10,
  check_timeout: 30,
  cache_results: true,
  cache_ttl: 3600,
  updated_at: new Date().toISOString(),
};
